// Full candidate profile — slide-over drawer from the right.
// Shows all extracted info, the score breakdown, Claude's
// strengths/weaknesses, editable recruiter notes, and status dropdown.

import { useEffect, useState } from 'react'
import {
  X, Star, Phone, Mail, MapPin, Calendar, Briefcase, GraduationCap,
  Award, Languages, FileText, Save, Loader, Tag,
} from 'lucide-react'
import { updateCandidate, CANDIDATE_STATUSES } from '../../lib/recruitmentStore'

const SCORE_CATEGORIES = [
  { key: 'skillsScore', label: 'Skills', max: 30 },
  { key: 'experienceScore', label: 'Experience', max: 25 },
  { key: 'educationScore', label: 'Education', max: 20 },
  { key: 'ageScore', label: 'Age Match', max: 10 },
  { key: 'locationScore', label: 'Location', max: 10 },
  { key: 'languageScore', label: 'Languages', max: 5 },
]

function scoreTier(score) {
  const s = Number(score) || 0
  if (s >= 90) return { label: 'Excellent', colour: 'text-green-400', bg: 'bg-green-900/40', border: 'border-green-700/50' }
  if (s >= 70) return { label: 'Good', colour: 'text-blue-400', bg: 'bg-blue-900/40', border: 'border-blue-700/50' }
  if (s >= 50) return { label: 'Average', colour: 'text-amber-400', bg: 'bg-amber-900/40', border: 'border-amber-700/50' }
  return { label: 'Weak', colour: 'text-red-400', bg: 'bg-red-900/40', border: 'border-red-700/50' }
}

export default function CandidateDrawer({ candidate, branch, onClose, addToast, onChange }) {
  const [notes, setNotes] = useState(candidate?.notes || '')
  const [savingNotes, setSavingNotes] = useState(false)
  const [status, setStatus] = useState(candidate?.status || 'pending')
  const [shortlisted, setShortlisted] = useState(!!candidate?.shortlisted)
  const [updating, setUpdating] = useState(false)

  useEffect(() => {
    setNotes(candidate?.notes || '')
    setStatus(candidate?.status || 'pending')
    setShortlisted(!!candidate?.shortlisted)
  }, [candidate?.id])

  if (!candidate) return null

  const tier = scoreTier(candidate.rankingScore)

  async function saveNotes() {
    if (notes === (candidate.notes || '')) return
    setSavingNotes(true)
    try {
      await updateCandidate(candidate.id, branch, { notes })
      addToast?.('Notes saved', 'success')
      onChange?.()
    } catch (err) {
      addToast?.(`Failed to save notes: ${err.message}`, 'error')
    } finally {
      setSavingNotes(false)
    }
  }

  async function handleStatusChange(newStatus) {
    setStatus(newStatus)
    setUpdating(true)
    try {
      // Status change often implies shortlist — auto-sync shortlisted flag
      const patch = { status: newStatus }
      if (newStatus === 'shortlisted') { patch.shortlisted = true; setShortlisted(true) }
      if (newStatus === 'rejected') { patch.shortlisted = false; setShortlisted(false) }
      await updateCandidate(candidate.id, branch, patch)
      onChange?.()
    } catch (err) {
      addToast?.(`Failed to update status: ${err.message}`, 'error')
    } finally {
      setUpdating(false)
    }
  }

  async function toggleShortlist() {
    const next = !shortlisted
    setShortlisted(next)
    setUpdating(true)
    try {
      await updateCandidate(candidate.id, branch, { shortlisted: next })
      onChange?.()
    } catch (err) {
      addToast?.(`Failed to update: ${err.message}`, 'error')
      setShortlisted(!next)
    } finally {
      setUpdating(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex justify-end" onClick={onClose}>
      <div
        className="absolute inset-0 bg-black/60"
        aria-hidden="true"
      />
      <div
        className="relative bg-gray-900 border-l border-gray-800 w-full max-w-2xl h-full overflow-y-auto"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="sticky top-0 bg-gray-900 border-b border-gray-800 px-6 py-4 flex items-start justify-between gap-3">
          <div className="flex-1 min-w-0">
            <h2 className="text-xl font-bold text-white truncate">{candidate.fullName}</h2>
            <p className="text-xs text-gray-500 mt-0.5 truncate">
              {candidate.email || 'No email'} · {candidate.phone || 'No phone'}
            </p>
            <div className="flex items-center gap-2 mt-2 flex-wrap">
              <div className={`inline-flex items-center gap-1.5 px-3 py-1 rounded text-sm font-semibold ${tier.bg} ${tier.colour} border ${tier.border}`}>
                {candidate.rankingScore || 0}/100 · {tier.label}
              </div>
              {candidate.recommendation && (
                <span className="text-xs text-gray-300 bg-gray-800 border border-gray-700 px-2 py-1 rounded">
                  {candidate.recommendation}
                </span>
              )}
              {candidate.analysisFailed && (
                <span className="text-xs text-red-400 bg-red-950/40 border border-red-800/50 px-2 py-1 rounded">
                  Analysis failed
                </span>
              )}
            </div>
          </div>
          <button onClick={onClose} className="text-gray-500 hover:text-white" aria-label="Close">
            <X size={20} />
          </button>
        </div>

        {/* Actions bar */}
        <div className="px-6 py-3 border-b border-gray-800 flex items-center gap-2 flex-wrap">
          <label className="text-xs text-gray-400">Status</label>
          <select
            value={status}
            onChange={e => handleStatusChange(e.target.value)}
            disabled={updating}
            className="bg-gray-800 border border-gray-700 rounded px-2 py-1 text-xs text-white"
          >
            {CANDIDATE_STATUSES.map(s => (
              <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>
            ))}
          </select>
          <button
            onClick={toggleShortlist}
            disabled={updating}
            className={`flex items-center gap-1 px-2.5 py-1 text-xs rounded border ${
              shortlisted
                ? 'bg-amber-900/40 text-amber-300 border-amber-700/50'
                : 'bg-gray-800 text-gray-400 border-gray-700 hover:text-white'
            }`}
          >
            <Star size={12} fill={shortlisted ? 'currentColor' : 'none'} />
            {shortlisted ? 'Shortlisted' : 'Shortlist'}
          </button>
          {updating && <Loader size={13} className="animate-spin text-gray-500" />}
        </div>

        {/* Body */}
        <div className="px-6 py-5 space-y-6">
          {/* Score breakdown */}
          {candidate.rankingBreakdown && (
            <section>
              <h3 className="text-xs font-semibold uppercase tracking-wider text-gray-400 mb-3">Score Breakdown</h3>
              <div className="space-y-2">
                {SCORE_CATEGORIES.map(cat => {
                  const value = Number(candidate.rankingBreakdown[cat.key]) || 0
                  const pct = (value / cat.max) * 100
                  return (
                    <div key={cat.key} className="flex items-center gap-3 text-xs">
                      <span className="text-gray-300 w-24">{cat.label}</span>
                      <div className="flex-1 bg-gray-800 rounded-full h-2 overflow-hidden">
                        <div className="bg-brand-500 h-full rounded-full" style={{ width: `${Math.min(100, Math.max(0, pct))}%` }} />
                      </div>
                      <span className="text-gray-400 font-mono w-12 text-right">{value}/{cat.max}</span>
                    </div>
                  )
                })}
                <div className="flex items-center gap-3 text-xs pt-2 mt-2 border-t border-gray-800">
                  <span className="text-white font-semibold w-24">TOTAL</span>
                  <div className="flex-1" />
                  <span className="text-white font-semibold font-mono w-12 text-right">{candidate.rankingScore || 0}/100</span>
                </div>
              </div>
            </section>
          )}

          {/* Assessment */}
          {(candidate.rankingReason || candidate.strengths?.length > 0 || candidate.weaknesses?.length > 0) && (
            <section>
              <h3 className="text-xs font-semibold uppercase tracking-wider text-gray-400 mb-2">Assessment</h3>
              {candidate.rankingReason && (
                <p className="text-sm text-gray-300 italic">"{candidate.rankingReason}"</p>
              )}
              {(candidate.strengths?.length > 0 || candidate.weaknesses?.length > 0) && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-3">
                  {candidate.strengths?.length > 0 && (
                    <div>
                      <p className="text-[11px] font-semibold text-green-400 mb-1">STRENGTHS</p>
                      <ul className="space-y-1">
                        {candidate.strengths.map((s, i) => (
                          <li key={i} className="text-xs text-gray-300">• {s}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {candidate.weaknesses?.length > 0 && (
                    <div>
                      <p className="text-[11px] font-semibold text-amber-400 mb-1">WEAKNESSES</p>
                      <ul className="space-y-1">
                        {candidate.weaknesses.map((w, i) => (
                          <li key={i} className="text-xs text-gray-300">• {w}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              )}
            </section>
          )}

          {/* Contact */}
          <section>
            <h3 className="text-xs font-semibold uppercase tracking-wider text-gray-400 mb-2">Contact</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-y-1.5 text-xs text-gray-300">
              {candidate.phone && <div className="flex items-center gap-2"><Phone size={12} className="text-gray-500" /> {candidate.phone}</div>}
              {candidate.email && <div className="flex items-center gap-2"><Mail size={12} className="text-gray-500" /> {candidate.email}</div>}
              {candidate.location && <div className="flex items-center gap-2"><MapPin size={12} className="text-gray-500" /> {candidate.location}</div>}
              {candidate.age != null && <div className="flex items-center gap-2"><Calendar size={12} className="text-gray-500" /> {candidate.age} years</div>}
            </div>
          </section>

          {/* Experience */}
          {candidate.experience?.length > 0 && (
            <section>
              <h3 className="text-xs font-semibold uppercase tracking-wider text-gray-400 mb-2 flex items-center gap-1">
                <Briefcase size={12} /> Experience
              </h3>
              <div className="space-y-2">
                {candidate.experience.map((exp, i) => (
                  <div key={i} className="border-l-2 border-gray-700 pl-3">
                    <p className="text-sm text-white">{exp.role || '(unknown role)'}</p>
                    <p className="text-xs text-gray-400">
                      {exp.company || '(unknown company)'}{exp.duration ? ` · ${exp.duration}` : ''}
                    </p>
                    {exp.responsibilities?.length > 0 && (
                      <ul className="mt-1 space-y-0.5">
                        {exp.responsibilities.slice(0, 3).map((r, j) => (
                          <li key={j} className="text-[11px] text-gray-500">• {r}</li>
                        ))}
                      </ul>
                    )}
                  </div>
                ))}
              </div>
            </section>
          )}

          {/* Education */}
          {candidate.education?.length > 0 && (
            <section>
              <h3 className="text-xs font-semibold uppercase tracking-wider text-gray-400 mb-2 flex items-center gap-1">
                <GraduationCap size={12} /> Education
              </h3>
              <div className="space-y-1">
                {candidate.education.map((ed, i) => (
                  <div key={i} className="text-xs text-gray-300">
                    <span className="text-white">{ed.qualification || '(unknown qualification)'}</span>
                    {ed.field && <span className="text-gray-400"> in {ed.field}</span>}
                    {ed.institution && <span className="text-gray-500"> · {ed.institution}</span>}
                    {ed.year && <span className="text-gray-500"> ({ed.year})</span>}
                  </div>
                ))}
              </div>
            </section>
          )}

          {/* Skills / Languages / Certifications */}
          {(candidate.skills?.length > 0 || candidate.languages?.length > 0 || candidate.certifications?.length > 0) && (
            <section className="space-y-3">
              {candidate.skills?.length > 0 && (
                <div>
                  <h4 className="text-xs font-semibold text-gray-400 mb-1.5 flex items-center gap-1">
                    <Tag size={11} /> Skills
                  </h4>
                  <div className="flex flex-wrap gap-1.5">
                    {candidate.skills.map(s => (
                      <span key={s} className="bg-gray-800 text-gray-300 text-[11px] px-2 py-0.5 rounded border border-gray-700">{s}</span>
                    ))}
                  </div>
                </div>
              )}
              {candidate.languages?.length > 0 && (
                <div>
                  <h4 className="text-xs font-semibold text-gray-400 mb-1.5 flex items-center gap-1">
                    <Languages size={11} /> Languages
                  </h4>
                  <div className="flex flex-wrap gap-1.5">
                    {candidate.languages.map(l => (
                      <span key={l} className="bg-blue-950/40 text-blue-300 text-[11px] px-2 py-0.5 rounded border border-blue-900/50">{l}</span>
                    ))}
                  </div>
                </div>
              )}
              {candidate.certifications?.length > 0 && (
                <div>
                  <h4 className="text-xs font-semibold text-gray-400 mb-1.5 flex items-center gap-1">
                    <Award size={11} /> Certifications
                  </h4>
                  <div className="flex flex-wrap gap-1.5">
                    {candidate.certifications.map(c => (
                      <span key={c} className="bg-amber-950/40 text-amber-300 text-[11px] px-2 py-0.5 rounded border border-amber-900/50">{c}</span>
                    ))}
                  </div>
                </div>
              )}
            </section>
          )}

          {/* Recruiter notes */}
          <section>
            <h3 className="text-xs font-semibold uppercase tracking-wider text-gray-400 mb-2 flex items-center gap-1">
              <FileText size={12} /> Recruiter Notes
            </h3>
            <textarea
              rows={4}
              value={notes}
              onChange={e => setNotes(e.target.value)}
              onBlur={saveNotes}
              placeholder="Add notes about this candidate…"
              className="w-full bg-gray-800 border border-gray-700 rounded px-3 py-2 text-sm text-gray-200"
            />
            <div className="flex justify-end mt-1.5">
              <button
                onClick={saveNotes}
                disabled={savingNotes || notes === (candidate.notes || '')}
                className="flex items-center gap-1 text-xs text-gray-400 hover:text-white disabled:opacity-40"
              >
                {savingNotes ? <Loader size={11} className="animate-spin" /> : <Save size={11} />}
                Save notes
              </button>
            </div>
          </section>

          {candidate.analysisFailed && candidate.analysisError && (
            <div className="bg-red-950/30 border border-red-800/50 rounded p-3 text-xs text-red-300">
              <p className="font-semibold text-red-200 mb-1">Analysis failed</p>
              <p>{candidate.analysisError}</p>
              <p className="text-red-300/70 mt-1">Use "Re-score All" from the candidates panel to retry.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
